import { useCompanyApi } from '@/api/client/api-client';
import { HttpMethod } from '@/api/client/http';
import { notifyError } from '@/bugsnag';
import { csrfToken } from '@/components/Utils/Helper';
import type { DocumentTemplateOption } from '@/components/Views/Company/File/Documents/document-upload-utils';
import {
    buildDocumentUploadFormDataV1,
    buildDocumentUploadFormDataV2,
    buildESignEditFormData,
    isDocumentBinaryConfig,
    isDocumentTpsConfig,
    mapDocumentErrors,
    mergeErrors,
    routesSupportAddDocV2,
    routesSupportEsignV2,
} from '@/components/Views/Company/File/Documents/document-upload-utils';
import type {
    AvailableWireDoc,
    Client,
    ClientOption,
    DocumentActionRoutes,
    DocumentUploadErrorResponse,
    DocumentUploadResponse,
    FileDetails,
    IndividualDocumentErrors,
    TPSDocumentWithContext,
} from '@/components/Views/Company/File/file.types';
import {
    DocumentUploadValidationErrorKeys,
    type DocumentRequestPayload,
    type DocumentRequestState,
    type DocumentUploadConfig,
    type DocumentUploadMergedErrorKeys,
    type DocumentUploadPayload,
    type ESignEditRequest,
    type ESignEditResponse,
    type SendWireDocResponse,
} from '@/components/Views/Company/File/file.types';
import { useDocumentManagementService } from '@/services/document-management.service';
import { performStoreAction } from '@/stores/company/perform-store-action';
import { defineStore } from 'pinia';
import { v4 as uuidV4 } from 'uuid';
import type { Ref } from 'vue';
import { computed, reactive, ref } from 'vue';
const { DOCUMENT, DOCUMENT_RECIPIENTS, DOCUMENT_SIGNERS, DOCUMENT_SIGN_GENERAL } = DocumentUploadValidationErrorKeys;

export interface AddDocumentParams {
    documentId: DocumentUploadConfig['documentId'];
    file?: File;
    template?: DocumentTemplateOption;
    tpsDocument?: TPSDocumentWithContext;
    isSelected?: DocumentUploadConfig['isSelected'];
}

export const useFileDocumentStore = defineStore('fileDocumentStore', () => {
    const isLoadingAction = ref(false);
    const isPreparingESign = ref(false);
    const esignEditError = ref<DocumentUploadErrorResponse>();
    const firstLevelError = ref<string>();
    const routes = ref<DocumentActionRoutes>();
    const sendNotification = ref(true);

    const selectedDocumentTemplates = ref<DocumentTemplateOption[]>([]);

    // Document upload state
    const documentConfigs = reactive<Map<string, DocumentUploadConfig>>(new Map());

    // Document request state
    const newDocumentRequest = ref<DocumentRequestState>({
        selectedClients: [],
        documentNames: [],
    });

    // Send wire doc state
    const sendWireDoc = ref<{
        wireDocId: AvailableWireDoc['id'] | null;
        selectedClientIds: Client['id'][];
        notify: boolean;
    }>({
        wireDocId: null,
        selectedClientIds: [],
        notify: true,
    });

    const preSelectedWireDocId = ref<AvailableWireDoc['id']>();

    // Preselected clients for document actions (send wire, request doc, etc)
    const documentActionClients = ref<Client['id'][]>([]);

    const getClientDocumentOptions = (clients: Client[]): ClientOption[] => {
        return clients.map(client => ({
            id: client.id.toString(),
            value: client.id,
            label: `${client.first} ${client.last} (${client.email})`,
            email: client.email,
            role: client.role,
            first: client.first,
            last: client.last,
            tooltipText: client.email,
        }));
    };

    const getRequestWithLoading = <T>(url: string) => {
        const companyFetch = useCompanyApi({ auth: 'jwt' });
        const request = companyFetch<T>(url, {
            immediate: false,
            beforeFetch: ctx => {
                isLoadingAction.value = true;
                return ctx;
            },
            onFetchError(ctx) {
                notifyError(ctx.error, 'file-document.store:getRequestWithLoading', {
                    data: ctx.data,
                    url,
                });
                return ctx;
            },
        });
        request.onFetchFinally(() => (isLoadingAction.value = false));
        return request;
    };

    const uploadDocuments = async (sendNotification: boolean): Promise<DocumentUploadResponse> => {
        if (!routesSupportAddDocV2(routes.value)) {
            notifyError('Route setup invalid for document upload', 'fileDocumentStore::uploadDocuments', {
                routes: routes.value,
            });
            return { success: false };
        }
        const documentConfigList = Array.from(documentConfigs.values()).filter(config => config.isSelected);
        const { success: uploadSuccess, warning: uploadWarning } = await executeAndHandleErrors(
            documentConfigList,
            uploadToDocumentsRoute(routes.value.add_document_v2, {
                documents: documentConfigList,
                sendNotification,
            }),
        );

        // BE may return success with a warning, in which case a partial success happened
        const warning = `${uploadWarning ?? ''}`.trim() || undefined;
        updateFirstLevelError(warning);
        return {
            success: uploadSuccess && !warning,
        };
    };

    // Unselected documents stay in the form (so re-checking restores them) but must not influence the
    // submission: their files aren't uploaded and their recipients/signers must not pull clients onto the file.
    const selectedDocumentConfigs = computed(
        () => new Map(Array.from(documentConfigs).filter(([, config]) => config.isSelected)),
    );

    const getFormUploadFormDataV1 = (): FormData => {
        return buildDocumentUploadFormDataV1({
            documents: Array.from(selectedDocumentConfigs.value.values()).filter(
                config => config.isSelected && (isDocumentBinaryConfig(config) || isDocumentTpsConfig(config)),
            ),
            sendNotification: sendNotification.value,
        });
    };

    const executeAndHandleErrors = async (
        includedDocuments: DocumentUploadConfig[],
        request?: ReturnType<typeof uploadToDocumentsRoute>,
    ) => {
        if (!request) {
            return { success: true }; // no request means nothing had to be done (empty set of each type)
        }
        const { execute, error, response, data } = request;
        await execute();
        handleUploadErrors(includedDocuments, error, response);
        return {
            success: !error?.value,
            warning: data?.value?.warning,
        };
    };

    const handleUploadErrors = async (
        includedDocuments: DocumentUploadConfig[],
        error?: Ref,
        response?: Ref<Response | null>,
    ) => {
        if (!error?.value || !response?.value) {
            return;
        }
        try {
            const castError = (await response.value?.json()) as DocumentUploadErrorResponse;
            updateOperationErrors(castError, includedDocuments, { ignoreFirstLevel: true });
        } catch (e) {
            notifyError(e, 'fileDocumentStore::handleUploadErrors', {
                response,
            });
        }
    };

    const uploadToDocumentsRoute = (route: string, payload: DocumentUploadPayload) => {
        const formData = buildDocumentUploadFormDataV2(payload, csrfToken.value);
        const request = getRequestWithLoading<DocumentUploadResponse>(route);
        return request.post(formData);
    };

    const getESignEditUrl = (routes: DocumentActionRoutes, request: ESignEditRequest) => {
        const formData = buildESignEditFormData(request, csrfToken.value);
        const endpoint = request.templateId && routesSupportEsignV2(routes) ? routes.esign_edit_v2 : routes.esign_edit;
        const requestInstance = getRequestWithLoading<ESignEditResponse>(endpoint);
        return requestInstance.post(formData).json<ESignEditResponse>();
    };

    const getNextDocumentFormKey = (): string => {
        // must be unique for each document but also a serialized int to conform with BE validation rules.
        // assumes TPS documents will use a pre-defined id we need to honour, so get the
        // max of existing keys and add 1 for the next key
        const existingKeys = Array.from(documentConfigs.values())
            .map(config => parseInt(config.formKey, 10))
            .filter(num => !isNaN(num));
        const maxExistingKey = existingKeys.length > 0 ? Math.max(...existingKeys) : 0;
        return (maxExistingKey + 1).toString();
    };

    const addDocument = ({
        documentId,
        file,
        template,
        tpsDocument,
        isSelected = true, // by default, auto-select documents as soon as they are uploaded
    }: AddDocumentParams) => {
        const titleOrFallback = (title: string) => title || 'Untitled Document';
        if (!documentConfigs.has(documentId)) {
            const esignDefaultChecked = !!template;
            const baseConfig = {
                documentId,
                formKey: getNextDocumentFormKey(),
                recipients: [],
                needsEsignature: esignDefaultChecked,
                signers: [],
                esignEditId: undefined,
                esignEditUrl: undefined,
                esignStateInvalidationError: '',
                errors: {
                    [DOCUMENT]: [],
                    [DOCUMENT_RECIPIENTS]: [],
                    [DOCUMENT_SIGNERS]: [],
                    [DOCUMENT_SIGN_GENERAL]: [],
                },
                isSelected,
                mergedErrors: new Map<DocumentUploadMergedErrorKeys, string>(),
            };

            if (template) {
                return documentConfigs.set(documentId, {
                    ...baseConfig,
                    template,
                    title: titleOrFallback(template.label),
                });
            }
            if (file) {
                return documentConfigs.set(documentId, {
                    ...baseConfig,
                    file,
                    title: titleOrFallback(file.name),
                });
            }
            if (tpsDocument) {
                return documentConfigs.set(documentId, {
                    ...baseConfig,
                    formKey: tpsDocument.id, // for TPS docs, the form key must be the document id defined by the vendor
                    title: titleOrFallback(tpsDocument.name),
                    vendorInfo: {
                        vendor: tpsDocument.vendor,
                        vendor_order_id: tpsDocument.vendor_order_id,
                        vendor_transaction_id: tpsDocument.vendor_transaction_id,
                        order_document_id: tpsDocument.id,
                    },
                    // Resware documents arrive without a `value` (storage path); they are fetched
                    // by id + branch on the backend. Fall back to the id so the doc stays resolvable.
                    file_temp_path: tpsDocument.value || tpsDocument.id,
                });
            }
        }
    };

    const toggleDocumentSelected = (
        documentId: DocumentUploadConfig['documentId'],
        isSelected: DocumentUploadConfig['isSelected'],
    ) => {
        updateDocument(documentId, { isSelected });
    };

    const updateDocument = (documentId: DocumentUploadConfig['documentId'], updates: Partial<DocumentUploadConfig>) => {
        const existing = documentConfigs.get(documentId);
        if (existing) {
            documentConfigs.set(documentId, {
                ...existing,
                ...updates,
            });
        }
    };

    const updateFirstLevelError = (error?: string) => {
        firstLevelError.value = error;
    };

    const updateDocumentErrors = (documentId: DocumentUploadConfig['documentId'], errors: Partial<IndividualDocumentErrors>) => {
        const existing = documentConfigs.get(documentId);
        if (existing) {
            documentConfigs.set(documentId, {
                ...existing,
                errors: { ...existing.errors, ...errors },
                mergedErrors: mergeErrors(errors),
            });
        }
    };

    const updateEsignStateInvalidationError = (documentId: DocumentUploadConfig['documentId'], message: string) => {
        const existing = documentConfigs.get(documentId);
        if (existing) {
            existing.esignStateInvalidationError = message;
        }
    };

    const clearDocumentErrorByKeys = (
        documentId: DocumentUploadConfig['documentId'],
        errorKeys: DocumentUploadValidationErrorKeys[],
    ) => {
        updateDocumentErrors(
            documentId,
            errorKeys.reduce((acc, key) => ({ ...acc, [key]: [] }), {}),
        );
    };

    const updateOperationErrors = (
        errorResponse: DocumentUploadErrorResponse,
        includedDocuments: DocumentUploadConfig[],
        { ignoreFirstLevel = false }: { ignoreFirstLevel?: boolean } = {},
    ) => {
        updateFirstLevelError(ignoreFirstLevel ? undefined : errorResponse.message);

        includedDocuments.forEach((documentConfig, index) => {
            updateDocumentErrors(documentConfig.documentId, mapDocumentErrors(errorResponse, index));
        });
    };

    const updateOperationErrorForDocument = (
        documentId: DocumentUploadConfig['documentId'],
        errorResponse: DocumentUploadErrorResponse,
        { ignoreFirstLevel = false }: { ignoreFirstLevel?: boolean } = {},
    ) => {
        updateFirstLevelError(ignoreFirstLevel ? undefined : errorResponse.message);

        updateDocumentErrors(documentId, mapDocumentErrors(errorResponse, 0)); // index is fixed here since we target a specific document
    };

    const removeDocument = (documentId: DocumentUploadConfig['documentId']) => {
        documentConfigs.delete(documentId);
        selectedDocumentTemplates.value = selectedDocumentTemplates.value.filter(template => template.id !== documentId);
        updateFirstLevelError();
    };

    const clearAllDocuments = () => {
        documentConfigs.clear();
        updateFirstLevelError();
    };

    const getDocumentConfig = (documentId: DocumentUploadConfig['documentId']) => {
        return documentConfigs.get(documentId);
    };

    // Document request methods
    const initializeDocumentRequest = () => {
        clearDocumentRequest();
        addDocumentToRequest();
    };

    const addDocumentToRequest = () => {
        newDocumentRequest.value.documentNames.push({
            id: uuidV4(),
            requestedFileName: '',
        });
    };

    const removeDocumentFromRequest = (id: string) => {
        newDocumentRequest.value.documentNames = newDocumentRequest.value.documentNames.filter(doc => doc.id !== id);
    };

    const updateDocumentRequestName = (id: string, fileName: string) => {
        const doc = newDocumentRequest.value.documentNames.find(d => d.id === id);
        if (doc) {
            doc.requestedFileName = fileName.trim();
        }
    };

    const submitDocumentRequestToFile = (url: string) => {
        const payload: DocumentRequestPayload = {
            requested_file_names: newDocumentRequest.value.documentNames.map(doc => doc.requestedFileName.trim()).filter(Boolean),
            client_email_list: newDocumentRequest.value.selectedClients.map(client => client.email),
        };

        return performStoreAction<{ success: boolean }, DocumentRequestPayload>(url, HttpMethod.POST, payload, {
            immediate: false,
        });
    };

    const clearDocumentRequest = () => {
        newDocumentRequest.value = {
            selectedClients: [],
            documentNames: [],
        };
    };

    // Send wire doc methods
    const initializeSendWireDoc = () => {
        sendWireDoc.value = {
            wireDocId: null,
            selectedClientIds: [],
            notify: true,
        };
    };

    const updateSendWireDocState = (updates: Partial<typeof sendWireDoc.value>) => {
        sendWireDoc.value = {
            ...sendWireDoc.value,
            ...updates,
        };
    };

    const submitSendWireDoc = (url: string) => {
        const formData = new FormData();
        formData.append('_method', 'PATCH');
        formData.append('wireDoc_id', sendWireDoc.value.wireDocId!.toString());

        sendWireDoc.value.selectedClientIds.forEach(id => {
            formData.append('wireDoc_recipients[]', id.toString());
        });

        if (sendWireDoc.value.notify) {
            formData.append('notify', 'notify');
        }

        const request = getRequestWithLoading<SendWireDocResponse>(url);
        return request.post(formData).json();
    };

    /**
     * Context: the endpoint that sends wire docs removes any other clients that may
     * already have it.
     * We need to load the current client+wireDoc combination if it exists and pre-populate the form
     */
    const resolveWireDocPreSelection = (documents: FileDetails['documents']['data'], extraClients: Client['id'][] = []) => {
        const existingWireDoc = documents.find(doc => doc.type === 'wire_doc');
        preSelectedWireDocId.value = existingWireDoc?.document_id ?? undefined;

        const clientsWithWireDocs = documents.filter(doc => doc.type === 'wire_doc').map(doc => doc.client_id);
        const uniqueClientIds = [...new Set([...clientsWithWireDocs, ...extraClients])];
        documentActionClients.value = uniqueClientIds;
    };

    // Document templates
    const { getDocumentTemplates } = useDocumentManagementService();
    // TODO evaluate what to do with very big collections. BE textual search is not yet responding well
    // potentially load all pages and resolve after the last one is known.
    // potentially cache these in localstorage?
    const fetchTemplates = () =>
        getDocumentTemplates({
            page: 0,
            pageSize: 100,
        });

    return {
        isLoadingAction,
        routes,
        isPreparingESign,
        firstLevelError,
        sendNotification,
        esignEditError,

        // Document actions
        uploadDocuments,
        getESignEditUrl,

        // Document upload state
        documentConfigs,
        selectedDocumentConfigs,
        addDocument,
        toggleDocumentSelected,
        updateDocument,
        updateEsignStateInvalidationError,
        updateDocumentErrors,
        updateOperationErrors,
        updateOperationErrorForDocument,
        updateFirstLevelError,
        clearDocumentErrorByKeys,
        removeDocument,
        clearAllDocuments,
        getDocumentConfig,

        // Document request state
        newDocumentRequest,
        initializeDocumentRequest,
        addDocumentRequest: addDocumentToRequest,
        removeDocumentRequest: removeDocumentFromRequest,
        updateDocumentRequestName,
        submitDocumentRequest: submitDocumentRequestToFile,
        clearDocumentRequest,

        // Send wire doc state
        sendWireDoc,
        initializeSendWireDoc,
        updateSendWireDocState,
        submitSendWireDoc,

        // Preselected clients
        documentActionClients,
        preSelectedWireDocId,
        resolveWireDocPreSelection,

        // Client option helpers
        getClientDocumentOptions,

        // Document templates
        selectedDocumentTemplates,
        fetchTemplates,

        // Expose form data for add-file
        getFormUploadFormDataV1,
    };
});
